import { createContext, useContext, useState, useCallback, useEffect, useRef, startTransition, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export interface AppTab {
  id: string;
  path: string;
  label: string;
  pinned?: boolean;
  lastVisitedAt?: number;
}

const MAX_TABS = 15;
const INACTIVE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

interface TabContextValue {
  tabs: AppTab[];
  activeTabId: string | null;
  openTab: (path: string, label: string) => void;
  updateTabLabel: (path: string, label: string) => void;
  closeTab: (id: string) => void;
  switchTab: (id: string) => void;
  pinTab: (id: string) => void;
  unpinTab: (id: string) => void;
  closeOtherTabs: (id: string) => void;
  closeTabsToRight: (id: string) => void;
  closeAllTabs: () => void;
  reorderTabs: (fromIndex: number, toIndex: number) => void;
  duplicateTab: (id: string) => void;
}

const TabContext = createContext<TabContextValue | null>(null);

function storageKey(userId: string | null, companyId?: string | null) {
  if (userId && companyId) return `homie_open_tabs_${userId}_${companyId}`;
  return userId ? `homie_open_tabs_${userId}` : 'homie_open_tabs';
}
function activeKey(userId: string | null, companyId?: string | null) {
  if (userId && companyId) return `homie_active_tab_${userId}_${companyId}`;
  return userId ? `homie_active_tab_${userId}` : 'homie_active_tab';
}

/** Map common paths to readable labels */
const PATH_LABELS: Record<string, string> = {
  '/office': 'Dashboard',
  '/office/cases': 'Sager',
  '/office/customers': 'Kunder',
  '/office/quotes': 'Tilbud',
  '/office/invoices': 'Fakturaer',
  '/office/receipts': 'Bilag',
  '/office/payroll': 'Løn',
  '/office/schedule': 'Planlægning',
  '/office/shift-plan': 'Vagtplan',
  '/office/reports': 'Rapporter',
  '/office/accounting-export': 'Bogføring',
  '/office/users': 'Brugere',
  '/office/mileage': 'Kørsel',
  '/webshop': 'Dashboard',
  '/webshop/orders': 'Ordrer',
  '/webshop/products': 'Produkter',
  '/webshop/customers': 'Kunder',
  '/webshop/messages': 'Beskeder',
  '/webshop/invoices': 'Fakturaer',
  '/webshop/receipts': 'Bilag',
  '/webshop/reports': 'Rapporter',
  '/webshop/shipping': 'Forsendelse',
  '/webshop/reviews': 'Anmeldelser',
  '/notifications': 'Notifikationer',
  '/profile': 'Profil',
};

export function getDefaultLabel(path: string): string {
  return PATH_LABELS[path] || path.split('/').pop() || path;
}

function getDashboardPath(): string {
  const mode = sessionStorage.getItem('app_mode');
  if (mode === 'webshop') return '/webshop';
  if (mode === 'field') return '/field';
  return '/office';
}

function normalizeTabPath(path: string): string {
  const basePath = (path.split(/[?#]/)[0] || '/').trim();
  if (basePath === '/') return '/';
  return basePath.replace(/\/+$/, '');
}

function shouldRegisterTab(path: string): boolean {
  const normalizedPath = normalizeTabPath(path);

  if (normalizedPath === '/office' || normalizedPath === '/webshop' || normalizedPath === '/notifications' || normalizedPath === '/profile') {
    return false;
  }

  if (normalizedPath.endsWith('/new') || normalizedPath.endsWith('/edit')) {
    return false;
  }

  return true;
}

function dedupeTabs(rawTabs: AppTab[]): AppTab[] {
  const merged = new Map<string, AppTab>();

  for (const tab of rawTabs) {
    const normalizedPath = normalizeTabPath(tab.path);
    if (!shouldRegisterTab(normalizedPath)) continue;

    const normalizedTab: AppTab = {
      ...tab,
      id: normalizedPath,
      path: normalizedPath,
    };

    const existing = merged.get(normalizedPath);
    if (!existing) {
      merged.set(normalizedPath, normalizedTab);
      continue;
    }

    merged.set(normalizedPath, {
      ...existing,
      label: normalizedTab.label.length > existing.label.length ? normalizedTab.label : existing.label,
      pinned: Boolean(existing.pinned || normalizedTab.pinned),
    });
  }

  return Array.from(merged.values());
}

function loadTabs(userId: string | null, companyId?: string | null): AppTab[] {
  try {
    const raw = localStorage.getItem(storageKey(userId, companyId));
    if (!raw) return [];
    return dedupeTabs(JSON.parse(raw));
  } catch {
    return [];
  }
}

function saveTabs(tabs: AppTab[], userId: string | null, companyId?: string | null) {
  localStorage.setItem(storageKey(userId, companyId), JSON.stringify(tabs));
}

function loadActiveTab(userId: string | null, companyId?: string | null): string | null {
  const active = localStorage.getItem(activeKey(userId, companyId));
  return active ? normalizeTabPath(active) : null;
}

function saveActiveTab(id: string | null, userId: string | null, companyId?: string | null) {
  if (id) localStorage.setItem(activeKey(userId, companyId), id);
  else localStorage.removeItem(activeKey(userId, companyId));
}

/** Sort: pinned first, then unpinned in their order */
function sortTabs(tabs: AppTab[]): AppTab[] {
  const pinned = tabs.filter(t => t.pinned);
  const unpinned = tabs.filter(t => !t.pinned);
  return [...pinned, ...unpinned];
}

export function TabProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const companyId = user?.company_id ?? null;
  const prevUserIdRef = useRef<string | null>(userId);
  const prevCompanyIdRef = useRef<string | null>(companyId);

  const [tabs, setTabs] = useState<AppTab[]>(() => loadTabs(userId, companyId));
  const [activeTabId, setActiveTabId] = useState<string | null>(() => loadActiveTab(userId, companyId));
  const navigate = useNavigate();

  const navigateTransition = useCallback((path: string) => {
    startTransition(() => {
      navigate(path);
    });
  }, [navigate]);

  // Reload tabs when user or company changes
  useEffect(() => {
    if (prevUserIdRef.current === userId && prevCompanyIdRef.current === companyId) return;
    prevUserIdRef.current = userId;
    prevCompanyIdRef.current = companyId;
    setTabs(loadTabs(userId, companyId));
    setActiveTabId(loadActiveTab(userId, companyId));
  }, [userId, companyId]);

  // Cleanup legacy duplicate tabs once on mount
  useEffect(() => {
    setTabs(prev => {
      const normalized = dedupeTabs(prev);
      const unchanged =
        normalized.length === prev.length &&
        normalized.every((tab, idx) =>
          tab.id === prev[idx]?.id &&
          tab.path === prev[idx]?.path &&
          tab.label === prev[idx]?.label &&
          tab.pinned === prev[idx]?.pinned
        );

      return unchanged ? prev : normalized;
    });
  }, []);

  // Persist on change
  useEffect(() => {
    saveTabs(tabs, userId, companyId);
  }, [tabs, userId, companyId]);
  useEffect(() => {
    saveActiveTab(activeTabId, userId, companyId);
  }, [activeTabId, userId, companyId]);

  const openTab = useCallback((path: string, label: string) => {
    const normalizedPath = normalizeTabPath(path);

    if (!shouldRegisterTab(normalizedPath)) {
      navigateTransition(normalizedPath);
      return;
    }

    setTabs(prev => {
      const existing = prev.find(t => normalizeTabPath(t.path) === normalizedPath);

      if (existing) {
        setActiveTabId(existing.id);
        const needsUpdate = (label && existing.label !== label) || existing.lastVisitedAt !== Date.now();
        if (needsUpdate) {
          return prev.map(t =>
            t.id === existing.id
              ? { ...t, label: label || t.label, lastVisitedAt: Date.now() }
              : t
          );
        }
        return prev;
      }

      const newTab: AppTab = { id: normalizedPath, path: normalizedPath, label, lastVisitedAt: Date.now() };
      let updated = [...prev, newTab];

      // Evict oldest unpinned tab if over limit
      const unpinned = updated.filter(t => !t.pinned);
      if (unpinned.length > MAX_TABS) {
        const oldest = unpinned.reduce((a, b) => ((a.lastVisitedAt ?? 0) < (b.lastVisitedAt ?? 0) ? a : b));
        updated = updated.filter(t => t.id !== oldest.id);
      }

      setActiveTabId(newTab.id);
      return sortTabs(updated);
    });

    navigateTransition(normalizedPath);
  }, [navigateTransition]);

  const updateTabLabel = useCallback((path: string, label: string) => {
    const normalizedPath = normalizeTabPath(path);

    setTabs(prev => {
      let didUpdate = false;
      const updated = prev.map(tab => {
        if (normalizeTabPath(tab.path) !== normalizedPath || tab.label === label) return tab;
        didUpdate = true;
        return { ...tab, label };
      });
      return didUpdate ? updated : prev;
    });
  }, []);

  const closeTab = useCallback((id: string) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === id);
      if (!tab || tab.pinned) return prev; // can't close pinned

      const idx = prev.findIndex(t => t.id === id);
      // Also close child tabs (e.g. closing a case tab also closes its Quote Studio tab)
      const normalizedId = normalizeTabPath(id);
      const next = prev.filter(t => {
        if (t.id === id) return false;
        // Close child tabs that are sub-paths of the closed tab
        if (normalizeTabPath(t.id).startsWith(normalizedId + '/') && !t.pinned) return false;
        return true;
      });

      if (activeTabId === id) {
        if (next.length > 0) {
          const newIdx = Math.min(idx, next.length - 1);
          setActiveTabId(next[newIdx].id);
          navigateTransition(next[newIdx].path);
        } else {
          setActiveTabId(null);
          navigateTransition(getDashboardPath());
        }
      } else if (activeTabId && !next.find(t => t.id === activeTabId)) {
        // Active tab was a child that got closed
        if (next.length > 0) {
          const newIdx = Math.min(idx, next.length - 1);
          setActiveTabId(next[newIdx].id);
          navigateTransition(next[newIdx].path);
        } else {
          setActiveTabId(null);
          navigateTransition(getDashboardPath());
        }
      }
      return next;
    });
  }, [activeTabId, navigateTransition]);

  const switchTab = useCallback((id: string) => {
    const tab = tabs.find(t => t.id === id);
    if (tab) {
      setActiveTabId(id);
      setTabs(prev => prev.map(t => t.id === id ? { ...t, lastVisitedAt: Date.now() } : t));
      navigateTransition(tab.path);
    }
  }, [tabs, navigateTransition]);

  // Auto-close inactive unpinned tabs every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setTabs(prev => {
        const toClose = prev.filter(t =>
          !t.pinned &&
          t.id !== activeTabId &&
          t.lastVisitedAt &&
          now - t.lastVisitedAt > INACTIVE_TIMEOUT_MS
        );
        if (toClose.length === 0) return prev;
        const closedIds = new Set(toClose.map(t => t.id));
        return prev.filter(t => !closedIds.has(t.id));
      });
    }, 60_000);
    return () => clearInterval(interval);
  }, [activeTabId]);

  const pinTab = useCallback((id: string) => {
    setTabs(prev => sortTabs(prev.map(t => t.id === id ? { ...t, pinned: true } : t)));
  }, []);

  const unpinTab = useCallback((id: string) => {
    setTabs(prev => sortTabs(prev.map(t => t.id === id ? { ...t, pinned: false } : t)));
  }, []);

  const closeOtherTabs = useCallback((id: string) => {
    setTabs(prev => {
      const kept = prev.filter(t => t.id === id || t.pinned);
      if (!kept.find(t => t.id === activeTabId)) {
        const target = kept.find(t => t.id === id) || kept[0];
        if (target) {
          setActiveTabId(target.id);
          navigateTransition(target.path);
        }
      }
      return kept;
    });
  }, [activeTabId, navigateTransition]);

  const closeTabsToRight = useCallback((id: string) => {
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === id);
      const kept = prev.filter((t, i) => i <= idx || t.pinned);
      if (!kept.find(t => t.id === activeTabId)) {
        setActiveTabId(id);
        const tab = kept.find(t => t.id === id);
        if (tab) navigateTransition(tab.path);
      }
      return kept;
    });
  }, [activeTabId, navigateTransition]);

  const closeAllTabs = useCallback(() => {
    setTabs(prev => {
      const pinned = prev.filter(t => t.pinned);
      if (pinned.length > 0) {
        setActiveTabId(pinned[0].id);
        navigateTransition(pinned[0].path);
      } else {
        setActiveTabId(null);
        navigateTransition(getDashboardPath());
      }
      return pinned;
    });
  }, [navigateTransition]);

  const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
    setTabs(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  }, []);

  const duplicateTab = useCallback((id: string) => {
    const tab = tabs.find(t => t.id === id);
    if (tab) {
      // Just switch to it since it's the same path
      switchTab(id);
    }
  }, [tabs, switchTab]);

  return (
    <TabContext.Provider value={{
      tabs,
      activeTabId,
      openTab,
      updateTabLabel,
      closeTab,
      switchTab,
      pinTab,
      unpinTab,
      closeOtherTabs,
      closeTabsToRight,
      closeAllTabs,
      reorderTabs,
      duplicateTab,
    }}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabContext() {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error('useTabContext must be used within TabProvider');
  return ctx;
}

